import type { DemoSessionActivity } from '@carboncopyco/data';
import { RecordingTypes } from '@carboncopyco/types';
import { TextBase, TooltipBubble, UserBasicInfo } from '@carboncopyco/ui';
import { secondsToHHmmss } from '@carboncopyco/utils';
import type { Col } from '../../../FixedTable';
import { ViewedScreens } from '../../../InfoPage/DemoPage/ActivitiesTab/ViewedScreens';
import { AssetNameLink } from '../../AssetNameLink';
import type { DashboardProps } from '../../Dashboard';
import { TimestampCol } from '../../SessionBreakdownComponents';

export const PresentationsTableCols: (
  demoPagePath: DashboardProps['demoPagePath'],
  playbookPagePath: DashboardProps['playbookPagePath']
) => Col<DemoSessionActivity>[] = (demoPagePath, playbookPagePath) => [
  {
    alignment: 'left',
    title: 'Date & time',
    width: 150,
    maxContentWidth: 130,
    render: ({ timestamp }) => <TimestampCol timestamp={timestamp} />,
  },
  {
    alignment: 'left',
    title: 'Presenter',
    width: 220,
    render: ({ presenter }) => (
      <UserBasicInfo
        user={presenter}
        textColor="text-ds-natural-600"
        truncateWidth={130}
        size="small"
        tooltip
      />
    ),
  },
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 120,
    maxContentWidth: 100,
    render: ({ forClient }) => (
      <TooltipBubble
        title={forClient}
        placement="top-start"
      >
        <div className="truncate">
          <TextBase
            label={forClient}
            color="text-ds-natural-600"
            ellipsis
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    alignment: 'left',
    title: 'Demo name',
    width: 200,
    render: ({ asset }) => {
      const { assetType, name, assetId, workspaceId } = asset;
      return (
        <AssetNameLink
          to={
            assetType === 'playbook'
              ? playbookPagePath(assetId, workspaceId)
              : demoPagePath(assetId, workspaceId)
          }
          label={name}
          asset={asset}
          assetType={assetType}
        />
      );
    },
  },
  {
    alignment: 'right',
    title: 'Duration',
    width: 120,
    render: (presentation) => (
      <TextBase
        label={secondsToHHmmss(presentation.durationInSeconds)}
        color="text-ds-natural-600"
      />
    ),
  },
  {
    alignment: 'right',
    title: 'Viewed screens',
    width: 130,
    render: ({
      sessionId,
      workspaceId,
      organizationId,
      asset,
      screensVisited,
    }) => {
      const isOverlay = asset.recordingType === RecordingTypes.overlay;
      return (
        asset.assetType === 'demo' && (
          <ViewedScreens
            sessionId={sessionId}
            workspaceId={workspaceId}
            organizationId={organizationId}
            isOverlay={isOverlay}
            screensVisited={screensVisited}
          />
        )
      );
    },
  },
];
